import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { MapPin, Clock, Users, ArrowRight } from 'lucide-react';
import type { Trip } from '@/lib/mockData';

interface TripCardProps {
  trip: Trip;
  viewMode?: 'grid' | 'list';
}

const difficultyStyles: Record<string, string> = {
  Easy: 'badge-difficulty-easy',
  Moderate: 'badge-difficulty-moderate',
  Challenging: 'badge-difficulty-challenging',
  Extreme: 'badge-difficulty-extreme',
};

export default function TripCard({ trip, viewMode = 'grid' }: TripCardProps) {
  const seatsLeft = trip.totalSeats - trip.bookedSeats;
  const seatsPercent = (trip.bookedSeats / trip.totalSeats) * 100;
  const isSoldOut = seatsLeft === 0;
  const isUrgent = seatsLeft > 0 && seatsLeft <= 4;

  if (viewMode === 'list') {
    return (
      <Link href="/trip-detail-page" className="block">
        <div className="card-base trip-card-hover flex gap-0 overflow-hidden">
          <div className="relative w-48 shrink-0">
            <AppImage
              src={trip.images[0]}
              alt={`${trip.title} — ${trip.location}, ${trip.state}`}
              fill
              className="object-cover"
              sizes="192px"
            />
            {isSoldOut && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <span className="text-white font-bold text-sm bg-red-600 px-3 py-1 rounded-full">Sold Out</span>
              </div>
            )}
          </div>
          <div className="flex-1 p-5 flex flex-col justify-between">
            <div>
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-bold text-[#1A3C5E] text-base leading-snug">{trip.title}</h3>
                <span className={difficultyStyles[trip.difficulty]}>{trip.difficulty}</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-500 text-sm mb-3">
                <MapPin size={13} />
                <span>{trip.location}, {trip.state}</span>
              </div>
              <p className="text-gray-600 text-sm line-clamp-2">{trip.shortDesc}</p>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1"><Clock size={13} />{trip.duration} days</span>
                <span className="flex items-center gap-1"><Users size={13} />Max {trip.maxGroupSize}</span>
                {!isSoldOut && (
                  <span className={isUrgent ? 'seats-urgent' : 'seats-available'}>
                    {seatsLeft} seat{seatsLeft !== 1 ? 's' : ''} left
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-gray-400">per person</p>
                  <p className="font-bold text-[#E8440A] font-mono text-lg">₹{trip.price.toLocaleString('en-IN')}</p>
                </div>
                <span className="btn-primary text-sm px-4 py-2">Book <ArrowRight size={14} /></span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link href="/trip-detail-page" className="block">
      <div className="card-base trip-card-hover overflow-hidden flex flex-col h-full">
        {/* Image */}
        <div className="relative h-48 overflow-hidden">
          <AppImage
            src={trip.images[0]}
            alt={`${trip.title} — ${trip.location}, ${trip.state}`}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

          {/* Badges overlay */}
          <div className="absolute top-3 left-3 flex gap-1.5">
            <span className={difficultyStyles[trip.difficulty]}>{trip.difficulty}</span>
            {trip.featured && (
              <span className="bg-[#E8440A] text-white text-xs font-semibold px-2.5 py-1 rounded-full">Featured</span>
            )}
          </div>

          {/* Seats badge */}
          <div className="absolute top-3 right-3">
            {isSoldOut ? (
              <span className="bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded-full">Sold Out</span>
            ) : isUrgent ? (
              <span className="seats-urgent">{seatsLeft} left!</span>
            ) : (
              <span className="seats-available">{seatsLeft} seats</span>
            )}
          </div>

          {/* Duration bottom */}
          <div className="absolute bottom-3 left-3 flex items-center gap-1 text-white text-xs font-medium">
            <Clock size={12} />
            <span>{trip.duration} days</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-1">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <h3 className="font-bold text-[#1A3C5E] text-sm leading-snug line-clamp-2 flex-1">{trip.title}</h3>
          </div>
          <div className="flex items-center gap-1 text-gray-500 text-xs mb-3">
            <MapPin size={11} />
            <span>{trip.location}, {trip.state}</span>
          </div>
          <p className="text-gray-500 text-xs line-clamp-2 flex-1 mb-4">{trip.shortDesc}</p>

          {/* Seats progress */}
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>{trip.bookedSeats}/{trip.totalSeats} booked</span>
              <span className={isUrgent ? 'text-red-500 font-semibold' : 'text-gray-400'}>{seatsLeft} left</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  seatsPercent >= 90 ? 'bg-red-500' : seatsPercent >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${seatsPercent}%` }}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-400">from</p>
              <p className="font-bold text-[#E8440A] font-mono text-base">₹{trip.price.toLocaleString('en-IN')}</p>
            </div>
            <button className="btn-primary text-xs px-3 py-2">
              View Trip <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}